import { createAuthJourneyScope } from '@/lib/auth/authJourneyClient'

type FetchResponse = {
  ok: boolean
  json?: () => Promise<unknown>
}

type Fetcher = (input: string, init: RequestInit) => Promise<FetchResponse>
const RECOVERY_ERROR = 'Password update is temporarily unavailable.'
const allowedErrors = new Set([
  RECOVERY_ERROR,
  'Password update is unavailable.',
  'Recovery link is invalid or expired.',
  'Password must be between 8 and 4096 characters.',
  'Password update could not be confirmed. Request a new recovery link.',
  'An additional authentication check is required. Contact support for account recovery.',
  'This account uses a managed identity provider. Contact your administrator.'
])
function recoveryError(error: unknown) {
  return error instanceof Error && allowedErrors.has(error.message) ? error.message : RECOVERY_ERROR
}

function objectPayload(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

export async function submitRecoveredPassword(fetcher: Fetcher, password: string) {
  const response = await fetcher('/auth/update-password/password', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ password })
  }).catch(() => {
    throw new Error(RECOVERY_ERROR)
  })
  const payload = objectPayload(await response.json?.().catch(() => null))
  if (!response.ok || payload?.success !== true) {
    throw new Error(
      typeof payload?.error === 'string' && allowedErrors.has(payload.error)
        ? payload.error
        : RECOVERY_ERROR
    )
  }
  return { success: true as const }
}

/** Password authority remains entirely in the server's single-use receipt. */
export function createPasswordRecoveryActionScope(owner: { userId: string; sessionId: string }) {
  const scope = createAuthJourneyScope(owner.sessionId)
  let seenOwner = false
  let ownerChanged = false
  return {
    activate: () => {
      if (!ownerChanged) scope.activate(owner.sessionId)
    },
    dispose: () => scope.dispose(),
    observeActor(actor: string | null) {
      if (ownerChanged) return
      if (actor === owner.userId) seenOwner = true
      else if (seenOwner || actor !== null) {
        ownerChanged = true
        scope.dispose()
      }
    },
    isOwnerChanged: () => ownerChanged,
    run: scope.run
  }
}

export async function completeRecoveredPassword(
  deps: {
    submit: () => Promise<unknown>
    isCurrent: () => boolean
    getCurrentActor: () => string | null
  },
  owner: { userId: string; sessionId: string }
): Promise<{ success: true } | { success: false; error: string }> {
  const stale = { success: false as const, error: '' }
  const current = () => deps.isCurrent() && deps.getCurrentActor() === owner.userId
  if (
    !owner.userId ||
    !owner.sessionId ||
    !deps.isCurrent() ||
    deps.getCurrentActor() !== owner.userId
  )
    return stale
  try {
    await deps.submit()
  } catch (error) {
    return current() ? { success: false, error: recoveryError(error) } : stale
  }
  if (!current()) return stale

  // The admin writer already revokes the recovered account's provider sessions.
  // A browser sign-out would use a mutable shared session and could target a
  // different account. Server getUser guards reject the stale recovery cookies.
  return { success: true }
}

export async function clearPasswordRecoveryIntent(fetcher: Fetcher): Promise<void> {
  try {
    await fetcher('/auth/update-password/clear', {
      method: 'POST',
      credentials: 'same-origin'
    })
  } catch {
    // Clearing the short-lived recovery proof is best-effort during sign-out.
    // The server still binds and expires the proof independently.
  }
}
